export const CLIENT_TYPE = {
  ENTERPRISE: 1,
  PERSONAL: 2,
};

export const VIP_TYPE = {
  NONE: 0,
  ACTIVE: 1,
};

export const PLAN_BUTTON_ACTION = {
  NEW: "new",
  UPGRADE: "upgrade",
  ACTIVE: "active",
  CONTACT_ADMIN: "contact_admin",
};

export const MIN_SUBSCRIPTION_SEAT = 1;
export const MAX_SUBSCRIPTION_SEAT = 299;

export function normalizeSubscriptionSeat(value) {
  const seat = Number(value);
  const resolvedSeat = Number.isFinite(seat) ? Math.floor(seat) : MIN_SUBSCRIPTION_SEAT;
  return Math.min(
    MAX_SUBSCRIPTION_SEAT,
    Math.max(MIN_SUBSCRIPTION_SEAT, resolvedSeat),
  );
}

export function mapUserSubscriptionInfo(apiData = {}) {
  return {
    subscribed: Boolean(apiData.subscribed),
    planName: apiData.planName || "",
    seats: normalizeSubscriptionSeat(apiData.seats),
    periodType:
      apiData.periodType != null ? Number(apiData.periodType) : null,
    sellingPrice:
      apiData.sellingPrice != null && apiData.sellingPrice !== ""
        ? Number(apiData.sellingPrice)
        : null,
  };
}

function normalizeUpgradeStatus(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const status = Number(value);
  return Number.isNaN(status) ? null : status;
}

export function resolvePlanButtonAction({
  upgradeStatus,
}) {
  const status = normalizeUpgradeStatus(upgradeStatus);
  if (status === 0) {
    return PLAN_BUTTON_ACTION.NEW;
  }
  if (status === 1) {
    return PLAN_BUTTON_ACTION.UPGRADE;
  }
  if (status === 4) {
    return PLAN_BUTTON_ACTION.CONTACT_ADMIN;
  }

  return PLAN_BUTTON_ACTION.ACTIVE;
}

// 半年卡须排在年卡前，避免「半年卡」被年卡后缀误匹配
const CYCLE_LABEL_PATTERN = "(半年卡|年卡|季卡|月卡)";

export function stripCycleFromPlanName(planName) {
  let name = String(planName || "").trim();
  if (!name) {
    return "";
  }
  name = name.replace(
    new RegExp(`[·\\-\\s]*${CYCLE_LABEL_PATTERN}\\s*(?:套餐)?$`, "i"),
    "",
  );
  name = name.replace(
    new RegExp(`${CYCLE_LABEL_PATTERN}\\s*(?:套餐)?$`, "i"),
    "",
  );
  return name.trim();
}

export function normalizePlanDisplayName(planName) {
  return stripCycleFromPlanName(planName).replace(/版$/, "");
}

export function getPlanNameForCycle(plan, cycle) {
  if (!plan) {
    return "";
  }
  if (plan.names && plan.names[cycle]) {
    return plan.names[cycle];
  }
  return plan.name || "";
}

export function getPlanDisplayNames(plan) {
  if (!plan) {
    return [];
  }
  const names = new Set();
  if (plan.name) {
    names.add(plan.name);
  }
  if (plan.names) {
    Object.values(plan.names).forEach((name) => {
      if (name) {
        names.add(name);
      }
    });
  }
  return [...names];
}

export function matchPlanByName(planList, planName) {
  const normalized = String(planName || "").trim();
  if (!normalized || !Array.isArray(planList)) {
    return null;
  }
  return (
    planList.find((item) =>
      getPlanDisplayNames(item).some((name) => normalized.startsWith(name)),
    ) || null
  );
}

export function matchPlanByNameAndPeriodType(planList, planName, periodType) {
  const cyclePlan = matchPlanByName(
    (planList || []).filter(
      (plan) => Number(plan.periodType) === Number(periodType),
    ),
    planName,
  );
  return cyclePlan || matchPlanByName(planList, planName);
}

export function getPlanButtonTextByAction(action) {
  if (action === PLAN_BUTTON_ACTION.NEW) {
    return "立即开通";
  }
  if (action === PLAN_BUTTON_ACTION.UPGRADE) {
    return "升级";
  }
  if (action === PLAN_BUTTON_ACTION.CONTACT_ADMIN) {
    return "请联系管理员";
  }
  return "已开通套餐";
}

export function formatPlanButtonText(plan, cycle) {
  const planName = normalizePlanDisplayName(plan && plan.name);
  const cycleLabel = getCycleLabel(cycle);
  if (planName && cycleLabel) {
    return `已开通${planName}${cycleLabel}套餐`;
  }
  return "已开通套餐";
}

export function formatActiveSubscriptionButtonText(subscriptionInfo = {}) {
  const planName = stripCycleFromPlanName(subscriptionInfo.planName || "");
  const cycleLabel = getCycleLabel(
    periodTypeToCycle(subscriptionInfo.periodType),
  );
  if (planName && cycleLabel) {
    return `已开通${planName}${cycleLabel}套餐`;
  }
  return "已开通套餐";
}

export function isPlanButtonActionEnabled(action) {
  return (
    action === PLAN_BUTTON_ACTION.NEW ||
    action === PLAN_BUTTON_ACTION.UPGRADE
  );
}

const VIDEO_SECONDS_RATIO = 0.4;

export const CYCLE_MONTHS = {
  year: 12,
  halfYear: 6,
  season: 3,
  month: 1,
};

// periodType: 1月卡 2季卡 3年卡 4半年卡
export const PERIOD_TYPE = {
  month: 1,
  season: 2,
  year: 3,
  halfYear: 4,
};

export const CYCLE_LABELS = {
  year: "年卡",
  halfYear: "半年卡",
  season: "季卡",
  month: "月卡",
};

export function cycleToPeriodType(cycle) {
  return PERIOD_TYPE[cycle] || PERIOD_TYPE.month;
}

export function periodTypeToCycle(periodType) {
  const value = Number(periodType);
  if (value === PERIOD_TYPE.year) {
    return "year";
  }
  if (value === PERIOD_TYPE.halfYear) {
    return "halfYear";
  }
  if (value === PERIOD_TYPE.season) {
    return "season";
  }
  return "month";
}

export function getPeriodUnit(cycle) {
  if (cycle === "year") {
    return "年";
  }
  if (cycle === "halfYear") {
    return "半年";
  }
  if (cycle === "season") {
    return "季";
  }
  return "月";
}

export function formatCreditDate(value) {
  if (!value) {
    return "—";
  }
  const dateStr = String(value).slice(0, 10);
  const date = new Date(dateStr.replace(/-/g, "/"));
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

export function getCycleLabel(cycle) {
  return CYCLE_LABELS[cycle] || CYCLE_LABELS.month;
}

export function isApiSuccess(code) {
  const value = Number(code);
  return value === 200 || value === 0;
}

export function getPaymentTypeName(paymentMethod) {
  return Number(paymentMethod) === 1 ? "微信" : "支付宝";
}
export function getPaymentMethodType(paymentMethod) {
	console.log(paymentMethod,'2323233223')
  return Number(paymentMethod) === "wechat" ? "微信" : "支付宝";
}
export function getCycleMonths(cycle) {
  return CYCLE_MONTHS[cycle] || 1;
}

export function getCyclePoints(monthlyIntegral, cycle) {
  return Number(monthlyIntegral || 0) * getCycleMonths(cycle);
}

export function buildPlanIntegrals(monthlyIntegral) {
  const monthly = Number(monthlyIntegral || 0);
  return {
    month: monthly,
    season: monthly * 3,
    halfYear: monthly * 6,
    year: monthly * 12,
  };
}

export function getOriginalPriceFromIntegral(integral) {
  return Number(integral || 0) / 10;
}

export function getPointPriceYuan(cycleIntegral, cyclePrice) {
  const points = Number(cycleIntegral || 0);
  const price = Number(cyclePrice || 0);
  if (!points || !price) {
    return 0;
  }
  return price / points;
}

export function formatPrice(yuan) {
  const value = Number(yuan);
  if (Number.isNaN(value)) {
    return "0";
  }
  return Math.round(value).toLocaleString("zh-CN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export function formatPointPrice(yuan) {
  const value = Number(yuan);
  if (!value || Number.isNaN(value)) {
    return "—";
  }
  if (value >= 0.01) {
    return value.toFixed(3).replace(/\.?0+$/, "");
  }
  return value.toFixed(4).replace(/\.?0+$/, "");
}

export function formatDiscount(discount) {
  const value = Number(discount);
  if (!value || Number.isNaN(value)) {
    return "—";
  }
  return `${Math.round(value)}折`;
}

export function formatDiscountRatio(ratio) {
  const value = Number(ratio);
  if (!value || Number.isNaN(value)) {
    return "—";
  }
  if (value > 0 && value < 1) {
    return `${(value * 10).toFixed(1).replace(/\.0$/, "")}折`;
  }
  return formatDiscount(value);
}

export function fenToYuan(value) {
  return Number(value || 0) / 100;
}

/**
 * 将接口积分单价（分/积分，仅展示用）格式化为元，保留两位小数。
 * 公式：1积分 = integralUnitPrice / 100 元
 */
export function formatIntegralUnitPriceYuan(integralUnitPrice) {
  const fen = Number(integralUnitPrice);
  if (!fen || Number.isNaN(fen)) {
    return "—";
  }
  return (fen / 100).toFixed(2);
}

/**
 * 分转元后格式化展示。
 * 默认四舍五入到整数元；传入 { decimal: true } 时保留最多 3 位小数。
 */
export function formatPriceFromFen(fen, options = {}) {
  const yuan = fenToYuan(fen);
  if (Number.isNaN(yuan)) {
    return "0";
  }
  // 需要小数精度时（如补差价、月均价）保留小数；其余场景四舍五入到整数元
  if (options.decimal) {
    return yuan.toLocaleString("zh-CN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  }
  return formatPrice(yuan);
}

export function isUnlimitedActivityEndDate(endDate) {
  if (!endDate) {
    return false;
  }
  const yearMatch = String(endDate).trim().match(/^(\d{4})/);
  if (!yearMatch) {
    return false;
  }
  return Number(yearMatch[1]) >= 9999;
}

export function getActivityEndDateDisplay(endDate) {
  if (!endDate) {
    return "";
  }
  if (isUnlimitedActivityEndDate(endDate)) {
    return "活动无期限";
  }
  return `活动截止至 ${String(endDate).slice(0, 10)}`;
}

export function mapSubscriptionPlanMeta(apiData) {
  if (!apiData || Array.isArray(apiData)) {
    return {
      activityName: "",
      mainTitle: "",
      subTitle: "",
      endDate: "",
      quotaLimit: 0,
      subscribedCount: 0,
    };
  }

  return {
    activityName: apiData.activityName || "",
    mainTitle: apiData.mainTitle || "",
    subTitle: apiData.subTitle || "",
    endDate: apiData.endDate || "",
    quotaLimit: Number(apiData.quotaLimit || 0),
    subscribedCount: Number(apiData.subscribedCount || 0),
  };
}

function normalizeApiPlans(apiData) {
  if (Array.isArray(apiData)) {
    return apiData;
  }
  if (apiData && Array.isArray(apiData.plans)) {
    return apiData.plans;
  }
  return [];
}

function getPlanActivity(plan) {
  const activities = plan.activities;
  if (Array.isArray(activities) && activities.length > 0) {
    const activity = activities[0];
    return {
      mainTitle: activity.mainTitle || "",
      subtitle: activity.subtitle || "",
    };
  }
  return {
    mainTitle: plan.activityMainTitle || "",
    subtitle: plan.activitySubtitle || "",
  };
}

function mapLegacyPlanList(apiPlans) {
  const sorted = [...apiPlans].sort(
    (a, b) => Number(a.sortOrder) - Number(b.sortOrder),
  );

  return sorted.map((plan) => {
    const monthlyIntegral = Number(plan.monthlyIntegral || 0);
    const quarterlyIntegral = monthlyIntegral * 3;
    const yearlyIntegral = monthlyIntegral * 12;
    const integrals = buildPlanIntegrals(monthlyIntegral);
    const monthlyPrice = fenToYuan(plan.monthlyPrice);
    const quarterlyPrice = fenToYuan(plan.quarterlyPrice);
    const yearlyPrice = fenToYuan(plan.yearlyPrice);
    const models = plan.models || [];
    const activity = getPlanActivity(plan);

    return {
      id: String(plan.configId),
      configId: plan.configId,
      configIds: {
        month: plan.configId,
        season: plan.configId,
        year: plan.configId,
      },
      name: plan.name || "",
      level: Number(plan.sortOrder || 0),
      seat: normalizeSubscriptionSeat(plan.seat),
      monthlyIntegral,
      quarterlyIntegral,
      yearlyIntegral,
      integrals,
      monthlyIntegrals: {
        month: monthlyIntegral,
        season: monthlyIntegral,
        year: monthlyIntegral,
      },
      points: monthlyIntegral,
      discount: getPrimaryModelDiscount(models),
      discounts: {
        month: getPrimaryModelDiscount(models),
        season: getPrimaryModelDiscount(models),
        year: getPrimaryModelDiscount(models),
      },
      primaryModelName: getPrimaryModelName(models),
      activityMainTitle: activity.mainTitle,
      activitySubtitle: activity.subtitle,
      activityMainTitles: {
        month: activity.mainTitle,
        season: activity.mainTitle,
        year: activity.mainTitle,
      },
      activitySubtitles: {
        month: activity.subtitle,
        season: activity.subtitle,
        year: activity.subtitle,
      },
      models,
      videoCount: Math.round(monthlyIntegral * VIDEO_SECONDS_RATIO),
      prices: {
        year: yearlyPrice,
        season: quarterlyPrice,
        month: monthlyPrice,
      },
      originalPrices: {
        year: getOriginalPriceFromIntegral(yearlyIntegral),
        season: getOriginalPriceFromIntegral(quarterlyIntegral),
        month: getOriginalPriceFromIntegral(monthlyIntegral),
      },
      pointPrices: {
        year: getPointPriceYuan(yearlyIntegral, yearlyPrice),
        season: getPointPriceYuan(quarterlyIntegral, quarterlyPrice),
        month: getPointPriceYuan(monthlyIntegral, monthlyPrice),
      },
      recommended: Number(plan.recommended) === 1,
      recommendedByCycle: {
        month: Number(plan.recommended) === 1,
        season: Number(plan.recommended) === 1,
        year: Number(plan.recommended) === 1,
      },
      upgradeStatus: normalizeUpgradeStatus(plan.upgradeStatus),
      upgradeStatuses: {
        month: normalizeUpgradeStatus(plan.upgradeStatus),
        season: normalizeUpgradeStatus(plan.upgradeStatus),
        year: normalizeUpgradeStatus(plan.upgradeStatus),
      },
    };
  });
}

function mapPeriodPlanList(apiPlans) {
  return [...apiPlans]
    .sort((a, b) => {
      const periodDiff = Number(b.periodType || 0) - Number(a.periodType || 0);
      if (periodDiff !== 0) {
        return periodDiff;
      }
      return Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
    })
    .map((plan) => {
      const activity = getPlanActivity(plan);
      const monthlyIntegral = Number(plan.monthlyIntegral || 0);
      const totalIntegral =
        Number(plan.totalIntegral || 0) ||
        getCyclePoints(monthlyIntegral, periodTypeToCycle(plan.periodType));

      return {
        ...plan,
        id: String(plan.configId),
        configId: plan.configId,
        clientType: Number(plan.clientType || 0),
        periodType: Number(plan.periodType || 0),
        level: Number(plan.sortOrder || 0),
        sortOrder: Number(plan.sortOrder || 0),
        sellingPrice: Number(plan.sellingPrice || 0),
        seat: normalizeSubscriptionSeat(plan.seat),
        monthlyIntegral,
        totalIntegral,
        originalPrice: Number(plan.originalPrice || 0),
        discountRatio: Number(plan.discountRatio || 0),
        integralUnitPrice: Number(plan.integralUnitPrice || 0),
        recommended: Number(plan.recommended || 0),
        upgradeStatus: normalizeUpgradeStatus(plan.upgradeStatus),
        discount: formatDiscountRatio(plan.discountRatio),
        activityMainTitle: activity.mainTitle,
        activitySubtitle: activity.subtitle,
        points: monthlyIntegral,
        videoCount: Math.round(monthlyIntegral * VIDEO_SECONDS_RATIO),
      };
    });
}

export function getPrimaryModel(models) {
  if (!Array.isArray(models) || models.length === 0) {
    return null;
  }
  return (
    models.find(
      (item) => item.modelName && /seedance/i.test(item.modelName),
    ) || models[0]
  );
}

export function getPrimaryModelDiscount(models) {
  const model = getPrimaryModel(models);
  if (!model) {
    return "—";
  }
  return formatDiscount(model.discount);
}

export function getPrimaryModelName(models) {
  const model = getPrimaryModel(models);
  return model && model.modelName ? model.modelName : "模型";
}

export function clientTypeToAccountType(clientType) {
  return Number(clientType) === CLIENT_TYPE.ENTERPRISE
    ? "enterprise"
    : "personal";
}

export function accountTypeToClientType(accountType) {
  return accountType === "enterprise"
    ? CLIENT_TYPE.ENTERPRISE
    : CLIENT_TYPE.PERSONAL;
}

export function mapPlanListFromApi(apiData) {
  const apiPlans = normalizeApiPlans(apiData);
  if (apiPlans.length === 0) {
    return [];
  }

  if (apiPlans[0].monthlyPrice != null) {
    return mapLegacyPlanList(apiPlans);
  }

  return mapPeriodPlanList(apiPlans);
}

export function getUserFromStorage() {
  try {
    return JSON.parse(window.localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
}

export function isSubscriptionButtonHidden(user = getUserFromStorage()) {
  return Boolean(
    user &&
      (user.subscriptionButtonHidden === true ||
        Number(user.subscriptionButtonHidden) === 1),
  );
}

/** clientType 唯一读入口：以 localStorage.user.clientType 为准 */
export function getUserClientType() {
  const user = getUserFromStorage();
  if (!user || user.clientType == null) {
    return null;
  }
  return Number(user.clientType);
}

/** 更新 localStorage.user 字段（跳过 null/undefined，避免误清空 clientType） */
export function persistUserFields(fields = {}) {
  const user = getUserFromStorage();
  if (!user) {
    return;
  }
  Object.keys(fields).forEach((key) => {
    if (fields[key] != null) {
      user[key] = fields[key];
    }
  });
  window.localStorage.setItem("user", JSON.stringify(user));
}

/** 从 localStorage 同步 clientType / subscriptionAccountType 到 store */
export function syncSubscriptionAccountTypeFromUser(store) {
  const clientType = getUserClientType();
  if (clientType == null) {
    return;
  }
  const user = getUserFromStorage();
  store.commit(
    "setSubscriptionAccountType",
    clientTypeToAccountType(clientType),
  );
  store.commit("setUserClientType", clientType);
  if (user) {
    store.commit("setUserSubscriptionInfo", {
      vipType: Number(user.vipType || 0),
    });
  }
}
